import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { client } from './amplifyClient';
import { findBestUserRowByEmail, pickUserRecordForSession } from './resolveAppUser';

/** Resolve the same User.id the app uses for session + activity (may differ from Cognito sub). */
export async function resolveCanonicalUserId(cognitoUserId: string): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const idPayload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
    const emailRaw = (idPayload.email as string) || '';
    const email = emailRaw ? emailRaw.toLowerCase().trim() : '';

    let cognitoRow = null;
    try {
      const byId = await client.models.User.get({ id: cognitoUserId }, { authMode: 'apiKey' });
      cognitoRow = byId.data ?? null;
    } catch {
      /* use email row only */
    }

    const emailRow = email ? await findBestUserRowByEmail(client, email) : null;
    const merged = pickUserRecordForSession(cognitoRow, emailRow);
    return merged?.id ?? cognitoUserId;
  } catch {
    return cognitoUserId;
  }
}

/** Persist lastLogin on the canonical User row (not only Cognito sub). */
export async function touchLastLogin(userId: string): Promise<void> {
  const now = new Date().toISOString();
  try {
    await client.models.User.update(
      { id: userId, lastLogin: now },
      { authMode: 'apiKey' },
    );
  } catch {
    try {
      await client.models.User.update({ id: userId, lastLogin: now });
    } catch {
      /* non-blocking */
    }
  }
}

export async function touchLastLoginForCognitoUser(cognitoUserId: string): Promise<void> {
  const canonicalId = await resolveCanonicalUserId(cognitoUserId);
  await touchLastLogin(canonicalId);
}

export async function touchLastLoginForCurrentUser(): Promise<void> {
  try {
    const current = await getCurrentUser();
    if (!current?.userId) return;
    await touchLastLoginForCognitoUser(current.userId);
  } catch {
    /* not signed in */
  }
}
